<template>
  <div class="operation-report-show-body-form-item flex flex-col">
    <div
      class="form-item-label aisea-text-16-700-dark-gray"
      :class="{
        required: required,
        'is-editing': isEditing,
        'has-errors': hasErrors,
      }"
    >
      {{ label }}
    </div>

    <div class="form-item-value-unit-edit flex aisea-text-16-400-dark-gray">
      <div class="form-item-value-edit w-full">
        <treeselect
          v-model="value"
          :name="name"
          v-validate="vValidate"
          :options="_selectOptions"
          :multiple="false"
          placeholder=""
          noOptionsText="なし"
          class="mr-4 my-1"
          @input="onChange"
          @change="onChange"
          noResultsText="見つかりませんでした"
          :clearable="false"
          :disabled="disabled"
        ></treeselect>
      </div>
      <div
        class="form-item-value-unit flex-shrink-0 flex items-center"
        v-show="hasUnit"
      >
        {{ unit }}
      </div>
    </div>
    <div
      class="nullable-label flex items-center aisea-text-16-400-dark-gray"
      v-if="hasNullableLabel"
    >
      <vs-checkbox
        v-model="nullableChecked"
        :color="checkBoxColor"
        :disabled="disabled"
        @change="onChange"
      >
        <span>{{ nullableLabel }}</span>
      </vs-checkbox>
    </div>
  </div>
</template>

<script>
import isEmpty from "lodash/isEmpty";
import isString from "lodash/isString";
import isNil from "lodash/isNil";
import { ColorMixin } from "@/mixins";

const { AISEA_COLOR } = ColorMixin.data();

import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";

import mapKeys from "lodash/mapKeys";

const EVENTS = {
  CHANGE: "change",
};

export default {
  name: "FormItemSelect",
  components: {
    Treeselect,
  },
  props: {
    label: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String, // validation のために必要
      required: true,
    },
    initialValue: {
      type: String,
      required: false,
    },
    unit: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // 編集中かどうか
    isEditing: {
      type: Boolean,
      default: false,
    },

    nullableLabel: {
      type: String,
      required: false,
    },
    isNullableChecked: {
      type: Boolean,
      default: false,
    },
    hasReportFormVersionItemValue: {
      type: Boolean,
      default: false,
    },
    defaultValue: {
      type: String,
      required: false,
    },
    selectOptions: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      value: null,
      nullableChecked: false,
      isDefaultValueInserted: false, // default値を挿入したかどうか -> defaultValueの更新が無限ループになるのを防ぐのに使う
    };
  },
  watch: {
    initialValue: {
      handler() {
        const {
          hasReportFormVersionItemValue,
          defaultValue,
          initialValue,
          onChange,
        } = this;
        if (
          !hasReportFormVersionItemValue &&
          !isNil(defaultValue) &&
          defaultValue !== ""
        ) {
          if (this.isDefaultValueInserted) return;
          this.value = defaultValue;
          onChange(); // デフォルト値を使用する場合に、そのまま更新リクエストできるようにする
        } else {
          this.value = initialValue;
        }
        this.isDefaultValueInserted = true;
      },
      immediate: true,
      deep: true,
    },
    isNullableChecked: {
      handler() {
        this.nullableChecked = this.isNullableChecked;
      },
      immediate: true,
    },
    vValidate() {
      this.$nextTick(async () => {
        await this.$validator.validateAll(); // nullableLabel の変更時にバリデーションを効かせる
      });
    },
  },
  computed: {
    _selectOptions() {
      return this.selectOptions.map(option => {
        return {
          ...option,
          id: option.value, // 選択状態を表示するために必要
        };
      });
    },
    selectOptionMapByValue() {
      return mapKeys(this.selectOptions, "value"); // { [key: value]: SELECT_OPTION }
    },
    formatValue() {
      return this.value;
    },

    hasUnit() {
      return isString(this.unit) && !isEmpty(this.unit);
    },
    isNullable() {
      if (!this.hasNullableLabel) return !this.required;
      return this.nullableChecked === true;
    },

    vValidate() {
      const required = this.required && !this.isNullable ? "required|" : "";
      return `${required}`;
    },
    hasErrors() {
      return this.errors.has(this.name);
    },
    hasNullableLabel() {
      const { nullableLabel } = this;
      return isString(nullableLabel) && !isEmpty(nullableLabel);
    },
    checkBoxColor() {
      return AISEA_COLOR.BLUE;
    },
  },
  methods: {
    onChange() {
      const { disabled, value, nullableChecked } = this;
      if (disabled) return;
      this.$emit(EVENTS.CHANGE, { value, nullableChecked });
    },
  },

  beforeDestroy() {
    this.isDefaultValueInserted = false;
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

.operation-report-show-body-form-item {
  min-height: 90px;
  padding: 18px 0;
  row-gap: 4px;

  .form-item-value-unit-edit {
    gap: 18px;
    padding-right: 18px;
  }
  ::v-deep input {
    padding-top: 14px;
    padding-bottom: 14px;
  }

  .form-item-label.is-editing {
    &.has-errors {
      color: $aisea-red; // エラー発生時に項目名を赤くする
    }

    // 必須の赤い点
    &.required:after {
      content: " *";
      font-size: 14px;
      color: $aisea-red;
      font-weight: 700;
      font-family: $aisea-font-family;
    }
  }
  .nullable-label {
    gap: 20px;
  }

  ::v-deep .vue-treeselect__control {
    height: 50px !important;
  }

  ::v-deep .vue-treeselect--disabled {
    opacity: 0.6;
  }

  ::v-deep .vue-treeselect__single-value {
    font-size: 1.2rem !important;
    padding: 0.5rem !important;
  }

  ::v-deep .vue-treeselect--open .vue-treeselect__control {
    border-top-color: $aisea-blue !important;
    border-left-color: $aisea-blue !important;
    border-right-color: $aisea-blue !important;
  }

  ::v-deep .vue-treeselect__menu {
    box-shadow: 2px 5px 10px 0.5px #00000026;
    border-bottom-color: $aisea-blue !important;
    border-left-color: $aisea-blue !important;
    border-right-color: $aisea-blue !important;
  }
}
</style>
